import { createClient } from '@/lib/supabase/client';

export interface BacktestParams {
  symbol: string;
  timeframe: string;
  startDate: string;
  endDate: string;
  initialCapital: string;
  riskPerTrade: string;
  stopLossType: string;
  takeProfitType: string;
  minPatternScore: string;
  maxOpenTrades: string;
}

export interface BacktestResults {
  winRate: number;
  totalTrades: number;
  wins: number;
  losses: number;
  profitFactor: number;
  netReturn: number;
  netReturnPct: number;
  maxDrawdown: number;
  maxDrawdownPct: number;
  grossProfit: number;
  grossLoss: number;
  equityCurve: { month: string; equity: number }[];
}

export interface BacktestRun {
  id: string;
  name: string;
  savedAt: string;
  params: BacktestParams;
  results: BacktestResults;
  chartSnapshot?: string;
  notes?: string;
}

// ─── localStorage helpers ────────────────────────────────────────────────────
function lsLoad(): BacktestRun[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem('backtest_history');
    if (!raw) return [];
    return JSON.parse(raw) as BacktestRun[];
  } catch {
    return [];
  }
}

function lsSave(runs: BacktestRun[]): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem('backtest_history', JSON.stringify(runs));
}

// ─── DB row → BacktestRun ────────────────────────────────────────────────────
function rowToRun(row: any): BacktestRun {
  return {
    id: row.id,
    name: row.name,
    savedAt: row.saved_at,
    params: row.params as BacktestParams,
    results: row.results as BacktestResults,
    chartSnapshot: row.chart_snapshot || undefined,
    notes: row.notes || undefined,
  };
}

// ─── Supabase helpers ────────────────────────────────────────────────────────
async function getAuthUser() {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  return { supabase, user };
}

// ─── Public API ──────────────────────────────────────────────────────────────

export async function loadBacktestHistoryAsync(): Promise<BacktestRun[]> {
  try {
    const { supabase, user } = await getAuthUser();
    if (!user) return lsLoad();

    const { data, error } = await supabase
      .from('backtest_runs')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.log('loadBacktestHistoryAsync error:', error.message);
      return lsLoad();
    }

    return (data || []).map(rowToRun);
  } catch {
    return lsLoad();
  }
}

export function loadBacktestHistory(): BacktestRun[] {
  return lsLoad();
}

export async function saveBacktestRunAsync(run: BacktestRun): Promise<void> {
  try {
    const { supabase, user } = await getAuthUser();
    if (!user) {
      const existing = lsLoad();
      lsSave([run, ...existing]);
      return;
    }

    await supabase.from('backtest_runs').insert({
      id: run.id,
      user_id: user.id,
      name: run.name,
      saved_at: run.savedAt,
      params: run.params,
      results: run.results,
      chart_snapshot: run.chartSnapshot || null,
      notes: run.notes || '',
    });

    // Also keep localStorage in sync for offline access
    const existing = lsLoad();
    lsSave([run, ...existing]);
  } catch {
    const existing = lsLoad();
    lsSave([run, ...existing]);
  }
}

export function saveBacktestRun(run: BacktestRun): void {
  const existing = lsLoad();
  lsSave([run, ...existing]);
}

export async function deleteBacktestRunAsync(id: string): Promise<BacktestRun[]> {
  try {
    const { supabase, user } = await getAuthUser();
    if (!user) {
      const runs = lsLoad().filter((r) => r.id !== id);
      lsSave(runs);
      return runs;
    }

    await supabase.from('backtest_runs').delete().eq('id', id).eq('user_id', user.id);
    const runs = lsLoad().filter((r) => r.id !== id);
    lsSave(runs);
    return runs;
  } catch {
    const runs = lsLoad().filter((r) => r.id !== id);
    lsSave(runs);
    return runs;
  }
}

export function generateRunId(): string {
  return `bt-run-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
}

export function buildDefaultResults(params: BacktestParams): BacktestResults {
  const base = parseInt(params.initialCapital) || 25000;
  const variance = (Math.random() - 0.3) * 0.4;
  const netReturnPct = 40 + variance * 100;
  const netReturn = Math.round(base * (netReturnPct / 100));
  const wins = Math.round(50 + Math.random() * 20);
  const losses = Math.round(30 + Math.random() * 15);
  const grossProfit = Math.round(netReturn * 1.8);
  const grossLoss = Math.round(grossProfit - netReturn);
  const maxDrawdownPct = 10 + Math.random() * 15;
  const maxDrawdown = -Math.round(base * (maxDrawdownPct / 100));

  const months = ['Jan 25','Feb 25','Mar 25','Apr 25','May 25','Jun 25','Jul 25','Aug 25','Sep 25','Oct 25','Nov 25','Dec 25','Jan 26','Feb 26','Mar 26','Apr 26','May 26','Jun 26','Jul 26','Aug 26'];
  let equity = base;
  const equityCurve = months.map((month) => {
    equity = equity + (Math.random() - 0.35) * base * 0.08;
    return { month, equity: Math.round(equity) };
  });
  equityCurve[equityCurve.length - 1].equity = base + netReturn;

  return {
    winRate: Math.round((wins / (wins + losses)) * 1000) / 10,
    totalTrades: wins + losses,
    wins,
    losses,
    profitFactor: Math.round((grossProfit / grossLoss) * 100) / 100,
    netReturn,
    netReturnPct: Math.round(netReturnPct * 10) / 10,
    maxDrawdown,
    maxDrawdownPct: Math.round(maxDrawdownPct * 10) / 10,
    grossProfit,
    grossLoss,
    equityCurve,
  };
}
